import React from 'react';
import { Box, Chip, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FileThumbnail from './FileThumbnail';
import { FileAttachment } from '../../hooks/useChatMessages';
import { useRagErrorCodes } from '../../hooks/useRagErrorCodes';

interface MessageAttachmentsProps {
  /** Array de archivos adjuntos a mostrar */
  attachments: FileAttachment[];
  /** ID del thread (opcional, para refrescar URLs expiradas) */
  threadId?: string;
  /** Número máximo de archivos a mostrar (opcional) */
  maxDisplay?: number;
  /** Mostrar chip "Analizando documento..." (mientras N8N procesa los ficheros) */
  isAnalyzing?: boolean;
}

/**
 * Componente contenedor que muestra un grid de archivos adjuntos.
 * Renderiza miniaturas usando el componente FileThumbnail.
 *
 * Características:
 * - Grid responsive con flexbox
 * - Espaciado consistente (gap: 1.5)
 * - Maneja descarga de archivos
 * - Lazy rendering para performance
 *
 * ✅ CRITICAL: Key estable para prevenir desmontaje
 * Usa fileName-fileSize-index en lugar de file.id porque:
 * - Mensaje optimista: id = "temp-file-1234"
 * - Mensaje real: id = "a1b2c3d4-uuid"
 * Si la key cambia, React desmonta y monta nuevo componente,
 * perdiendo el estado de FileThumbnail (previousThumbnailUrl, imageLoaded)
 * y causando flicker visual al reemplazar thumbnail cliente → servidor.
 */
const MessageAttachments: React.FC<MessageAttachmentsProps> = ({
  attachments,
  threadId,
  maxDisplay,
  isAnalyzing = false,
}) => {
  const { t } = useTranslation();
  // #157: resolver compartido para traducir file.rag_error a un mensaje amigable
  // (mismo hook/taxonomía que VectorizedKnowledgePanel del lado rol → UX consistente).
  // Se llama UNA vez por mensaje y se pasa a cada miniatura (evita N instancias del hook).
  const { resolveFromRaw } = useRagErrorCodes();
  /**
   * Maneja la descarga de un archivo
   * Crea un elemento <a> temporal y simula un click
   */
  const handleDownload = (file: FileAttachment) => {
    let link: HTMLAnchorElement | null = null;
    try {
      link = document.createElement('a');
      link.href = file.fileUrl;
      link.download = file.fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      // Añadir al DOM temporalmente para compatibilidad
      document.body.appendChild(link);
      link.click();

      console.log(`[MessageAttachments] Descargando archivo: ${file.fileName}`);
    } catch (error) {
      console.error(`[MessageAttachments] Error al descargar archivo: ${file.fileName}`, error);
    } finally {
      // ✅ FIX: Safer cleanup - verify element is still in DOM before removing
      // Use queueMicrotask for better timing than setTimeout(0)
      if (link) {
        const elementToRemove = link;
        queueMicrotask(() => {
          try {
            if (elementToRemove.parentNode === document.body) {
              document.body.removeChild(elementToRemove);
            }
          } catch {
            // Ignore errors during cleanup (element may have been removed by browser)
          }
        });
      }
    }
  };

  // Validación: Si no hay attachments o el array está vacío, no renderizar nada
  if (!attachments || attachments.length === 0) {
    return null;
  }

  // Limitar el número de archivos a mostrar si se especifica maxDisplay
  const filesToDisplay = maxDisplay
    ? attachments.slice(0, maxDisplay)
    : attachments;

  const hasMoreFiles = maxDisplay && attachments.length > maxDisplay;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          mt: 1.5,
          mb: 0.5,
        }}
        role="region"
        aria-label="Archivos adjuntos"
      >
        {filesToDisplay.map((file, index) => (
          <FileThumbnail
            key={`${file.fileName}-${file.fileSize}-${index}`}
            file={file}
            threadId={threadId}
            onDownload={handleDownload}
            resolveRagError={resolveFromRaw}
          />
        ))}

        {/* Indicador si hay más archivos no mostrados */}
        {hasMoreFiles && (
          <Box
            sx={{
              width: 160,
              height: 160,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'action.hover',
              fontSize: '0.875rem',
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            +{attachments.length - maxDisplay!} más
          </Box>
        )}
      </Box>

      {isAnalyzing && (
        <Chip
          size="small"
          icon={<CircularProgress size={12} thickness={5} sx={{ color: 'inherit' }} />}
          label={t('chat.analyzingDocument', { defaultValue: 'Analizando documento...' })}
          sx={{
            mt: 0.5,
            mb: 0.5,
            bgcolor: (theme) => theme.palette.action.hover,
            color: 'text.secondary',
            fontSize: '0.75rem',
            // Spacing between the spinner icon and the label
            '& .MuiChip-icon': { ml: 1, mr: -0.5 },
          }}
          aria-live="polite"
        />
      )}
    </Box>
  );
};

export default MessageAttachments;
